import { useMutation } from '@tanstack/react-query';
import { ApproveApi } from 'apis/ApproveApis';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  IOrderApproval,
  IUserApproval,
  TGetUserApprovalDetail,
} from 'interfaces/Approve/Approve';
import path from 'routes/Path';
import { formatCurrency, toastError } from 'utils/constants/functions';
import ApprovalBoard, {
  IApprovalContentItem,
} from './Components/ApprovalBoard';
import ApproveDetailWrapper, {
  IUserApprovalInfo,
} from './Components/ApproveDetailWrapper';

const initData = {} as IUserApproval;
const initUserApproval = {} as IUserApprovalInfo;
const initOrderApproval = {} as IOrderApproval;
const initApprovalData = [] as IApprovalContentItem[];

const PriceOrderApprove = () => {
  const navigate = useNavigate();
  const queryParam = useQueryParam();

  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();

  const approvalId = queryParam.get('id');
  const [data, setData] = useState<IUserApproval>(initData);

  const [userApproval, setUserApproval] =
    useState<IUserApprovalInfo>(initUserApproval);
  const [orderApproval, setOrderApproval] =
    useState<IOrderApproval>(initOrderApproval);
  const [approvalData, setApprovalData] =
    useState<IApprovalContentItem[]>(initApprovalData);

  const { mutate: getUserApprovalById } = useMutation({
    mutationFn: (params: TGetUserApprovalDetail) =>
      ApproveApi.getUserApprovalById(params),
    onSuccess: (res) => {
      const item = res.data?.getUserApproval?.items?.[0];
      if (!Object.keys(item).length) {
        toastError('Không tìm thấy thông tin phê duyệt');
        return;
      }

      setData(item);
      getUserApprovalForOrderMutate(item.id);
      getOrderApprovalMutate(item.idConnect);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: getUserApprovalForOrderMutate,
    isPending: isGetUserApprovalForOrderPending,
  } = useMutation({
    mutationFn: (id: string) => ApproveApi.getUserApprovalForOrder(id),
    onSuccess: (res: any) => {
      if (res.data?.getUserApproval) {
        const item = (res.data.getUserApproval?.items ?? [])[0];

        setUserApproval({
          plantId: item?.plantId,
          dateSent: item?.createDate,
          // status: item?.tstatusApproval?.name,
          status: item?.tuserApprovalDetail?.find(
            (item: any) => item?.email === user?.email,
          )?.approvalYn,
          fullName: item?.tcreateUser?.fullName,
          jobTitle: item?.tcreateUser?.jobTitle,
          note: '',
        });

        return;
      }

      if (res?.errors)
        toastError(
          `Lỗi không thế lấy thông tin người gửi phê duyệt. ${res.errors[0]?.message}`,
        );
    },
    onError: (error) => {
      toastError(
        `Lỗi không thế lấy thông tin người gửi phê duyệt. ${error?.message ?? error}`,
      );
    },
  });

  const {
    mutate: getOrderApprovalMutate,
    isPending: isGetOrderApprovalPending,
  } = useMutation({
    mutationFn: (orderId: string) => ApproveApi.getOrderApproval(orderId),
    onSuccess: (res: any) => {
      if (res.data?.getOrderUserApproval) {
        setOrderApproval(res.data?.getOrderUserApproval);
        return;
      }

      if (res?.errors)
        toastError(
          `Lỗi không thế lấy thông tin phê duyệt giá bóng bảng kê. ${res.errors[0]?.message}`,
        );
    },
    onError: (error) => {
      toastError(
        `Lỗi không thế lấy thông tin phê duyệt giá bóng bảng kê. ${error?.message ?? error}`,
      );
    },
  });

  // const { mutate: updateApprovalStatus } = useMutation({
  //   mutationFn: (params: IUpdateApprovalStatus) =>
  //     ApproveApi.updateApprovalStatus(params),
  //   onSuccess: () => {
  //     toastSuccess('Thành công');

  //     if (!state?.id) return;
  //     getUserApprovalById(state.id);
  //   },
  //   onError: (error) => {
  //     console.log(error);
  //     toastError(`Phê duyệt thất bại: ${error.message}`);
  //   },
  // });

  const formatCurrencyVND = useCallback((value: number) => {
    const formattedValue = formatCurrency({ isVND: true, value: value });

    return formattedValue ? `${formattedValue} VND` : '-';
  }, []);

  const refetch = useCallback(() => {
    if (!approvalId) return;

    const params: TGetUserApprovalDetail = {
      // id: state?.id,
      id: approvalId,
      ...(user?.isAdmin || user?.isHO
        ? {}
        : {
            tuserApprovalDetail: user?.email,
          }),
    };

    getUserApprovalById(params);
  }, [approvalId, user]);

  useEffect(() => {
    // if (!Object.keys(state).length) {
    //   navigate(path.APPROVE);
    //   return;
    // }

    setTitle('Thoả thuận giá bóng');
    setBreadcrumb([
      { title: 'Phê duyệt', href: path.APPROVE },
      { title: 'Thoả thuận giá bóng' },
    ]);

    // if (!state?.id) return;
    // getUserApprovalById(state.id);
  }, []);

  useEffect(() => {
    setApprovalData([
      {
        title: 'Khách hàng',
        children: [
          {
            content: [
              {
                title: 'Mã KH SAP',
                content: orderApproval?.tcustomer?.sapCustomerId,
              },
              {
                title: 'Tên KH',
                content: orderApproval?.tcustomer?.storeName,
              },
            ],
          },
          {
            content: [
              {
                title: 'Mã KH DMS',
                content: orderApproval?.tcustomer?.id,
              },
              {
                title: 'Loại KH',
                content: orderApproval?.tcustomer?.trank?.name,
              },
            ],
          },
        ],
      },
      {
        title: 'Thông tin thanh toán',
        children: [
          {
            content: [
              {
                title: 'SL Sản phẩm',
                content: orderApproval?.totalQuantity,
              },
              {
                title: 'Giá bóng',
                content: formatCurrencyVND(orderApproval?.marketGoldPrice),
              },
              {
                title: 'Tiền Vàng',
                content: formatCurrencyVND(orderApproval?.totalGoldPrice),
              },
            ],
          },
          {
            content: [
              {
                title: 'Q10 ',
                content: orderApproval?.totalQ10,
              },
              {
                title: 'Giá bóng TT',
                content: formatCurrencyVND(orderApproval?.dealPrice),
              },
              {
                title: 'Chiết khấu',
                content: formatCurrencyVND(orderApproval?.discount),
              },
            ],
          },
        ],
      },
      {
        title: '',
        children: [],
      },
      {
        title: '',
        children: [
          {
            content: [
              {
                title: 'Tiền món',
                content: formatCurrencyVND(orderApproval?.totalProductPrice),
              },
              {
                title: 'T.T món',
                content: formatCurrencyVND(
                  orderApproval?.totalProductPrice && orderApproval?.finalPrice,
                ),
              },
            ],
          },
          {
            content: [
              {
                title: 'Tiền công ',
                content: formatCurrencyVND(orderApproval?.totalProcessPrice),
              },
              {
                title: 'T.T công ',
                content: formatCurrencyVND(
                  orderApproval?.totalProcessPrice && orderApproval?.finalPrice,
                ),
              },
            ],
          },
        ],
      },
      {
        index: 3,
        title: 'Ghi chú',
        children: [
          {
            content: [
              {
                title: '',
                content: orderApproval?.note,
              },
            ],
          },
        ],
      },
      {
        index: 4,
        title: 'Chi tiết đơn',
        children: [
          {
            content: [
              {
                title: '',
                content: (
                  <p
                    className="text-[#0077CC] font-semibold no-underline text-sm cursor-pointer"
                    onClick={() => {
                      navigate(
                        `${path.SALES_INVOICE_DETAIL}?orderId=${orderApproval?.id}&orderTypeId=${orderApproval?.orderTypeId}`,
                      );
                    }}
                  >
                    Xem chi tiết
                  </p>
                ),
              },
            ],
          },
        ],
      },
    ]);
  }, [orderApproval]);

  console.log(orderApproval);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // const handleApprove = () => {
  //   if (!state?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: true,
  //   });
  // };

  // const handleCorrectionRequest = () => {
  //   if (!data?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: false,
  //   });
  // };

  return (
    <ApproveDetailWrapper
      data={data}
      userApproval={userApproval}
      title={`Bảng kê bán hàng - ${data?.idConnect}`}
      loading={isGetUserApprovalForOrderPending || isGetOrderApprovalPending}
      refetch={refetch}
      // approvalId={data?.id}
      // approvalStatus={data?.statusApproval}
      // refetch={() => getUserApprovalById(state.id)}
      // onApprove={handleApprove}
      // onCorrectionRequest={handleCorrectionRequest}
    >
      <ApprovalBoard data={approvalData} />
    </ApproveDetailWrapper>
  );
};

export default PriceOrderApprove;
